import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { BffAuthResponse } from '@lucky7ventures/bff-types';

import LuckyDaysLogo from '@/components/UI/Logos/LuckyDaysLogo';
import PromptMessage from '@/components/UI/PromptMessage';
import { TranslatedRichText } from '@/components/TranslatedRichText';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { loginSuccess } from '@/store/actions/auth';
import { ModalLayout } from '@/features/modal/modals/ModalLayout';
import { BaseModalProps } from '@/features/modal/types';
import { LoginOtpStep } from '@/features/modal/modals/login/LoginOtpStep';
import { LoginSmsFirstStep } from '@/features/modal/modals/login-sms/LoginSmsFirstStep';

enum LoginStep {
  FirstStep,
  OtpStep,
}

interface Credentials {
  username: string;
  password: string;
}

export interface LoginSmsModalProps {
  loggedOut?: boolean;
  loggedOutIntlId?: string;
  email?: string;
}

export const LoginSmsModal = ({
  closeModal,
  loggedOut = false,
  loggedOutIntlId = undefined,
}: LoginSmsModalProps & Pick<BaseModalProps, 'closeModal'>): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [loginStep, setLoginStep] = useState(LoginStep.FirstStep);
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useAbsoluteNavigate();
  const location = useLocation();

  const genericError = () => {
    setError(intl.formatMessage({ id: 'error.support' }));
  };

  const onLoginFirstStep = (data: BffAuthResponse, credentials: Credentials) => {
    setError(null);
    // there can be different results of the login
    // 1. SUCCESSFUL - that means that the user is logged in and the flow can be redirected forward
    // 2. TWO_FACTOR_BY_MESSAGE_FIRST_STEP_SUCCESSFUL - that means that the user has entered username/password ok, but still needs an OTP
    if (data.result === 'SUCCESSFUL') {
      dispatch(loginSuccess(data.token, { blockReason: data.blockReason }));
      triggerGTag(GTagEvents.login_sms_success);
      closeModal();

      if (
        ['register', 'reset-password', 'forgot-password'].some(path =>
          location.pathname.includes(path),
        )
      ) {
        navigate('/');
      }

      return;
    }

    if (data.result === 'TWO_FACTOR_FIRST_STEP_SUCCESSFUL') {
      setCredentials(credentials);
      setLoginStep(LoginStep.OtpStep);
      triggerGTag(GTagEvents.login_2fa);
      return;
    }
    genericError();
  };

  const onLoginOtpStep = (data: BffAuthResponse) => {
    setError(null);
    // there can be different results of the login
    // 1. SUCCESSFUL - that means that the user is logged in and the flow can be redirected forward
    // 2. TWO_FACTOR_FIRST_STEP_SUCCESSFUL - that means that the user has entered username/password ok, but still needs an OTP
    if (data.result === 'SUCCESSFUL') {
      dispatch(loginSuccess(data.token, { blockReason: data.blockReason }));
      triggerGTag(GTagEvents.login_2fa_success);
      closeModal();
      if (
        ['register', 'reset-password', 'forgot-password'].some(path =>
          location.pathname.includes(path),
        )
      ) {
        navigate('/');
      }
      return;
    }

    triggerGTag(GTagEvents.login_2fa_error);
    genericError();
  };

  return (
    <ModalLayout close={closeModal}>
      <div className="text">
        <div className="mb-10 flex flex-col items-center justify-center">
          <LuckyDaysLogo />
        </div>
        {loggedOut && loginStep === LoginStep.FirstStep && (
          <>
            {loggedOutIntlId && (
              <p className="prose-sm mb-6 rounded-md bg-red-light/40 p-4 text-left leading-snug text-red-600 prose-a:underline">
                <TranslatedRichText id={loggedOutIntlId} />
              </p>
            )}
            {!loggedOutIntlId && (
              <p className="intro-text mb-6 text-sm font-semibold">
                {intl.formatMessage({ id: 'loggedout.text' })}
              </p>
            )}
          </>
        )}
        {error && (
          <div className="-mt-4">
            <PromptMessage type="error" message={error} />
          </div>
        )}
        {loginStep === LoginStep.FirstStep && (
          <LoginSmsFirstStep onSuccessfulLogin={onLoginFirstStep} />
        )}
        {loginStep === LoginStep.OtpStep && (
          <LoginOtpStep
            username={credentials!.username}
            password={credentials!.password}
            onSuccessfulOtp={onLoginOtpStep}
          />
        )}
      </div>
    </ModalLayout>
  );
};
